import {OS, type Architecture, type OSItem} from '@github-ui/get-os'

import {
  resolveDownloadActions,
  type OSAwareDownloadAction,
  type ResolvedDownloadActions,
} from '../../OSAwareDownloadActionMenu/resolve-download-actions'
import type {DownloadOption} from '../../../schemas/contentful/contentTypes/downloadOption'
import type {DownloadOptions} from '../../../schemas/contentful/contentTypes/downloadOptions'

// The DownloadOption platform values are authored to match @github-ui/get-os's
// OS enum 1:1, but we map explicitly (rather than casting) so a future
// divergence between the two fails obviously instead of silently.
const PLATFORM_TO_OS: Record<DownloadOption['fields']['platform'], OS> = {
  Android: OS.Android,
  iOS: OS.iOS,
  Linux: OS.Linux,
  macOS: OS.macOS,
  Windows: OS.Windows,
}

export type NormalizedDownloadOption = OSItem & {
  option: DownloadOption
}

export type ResolvedDownloadOptions = {
  /** The best match for the resolved OS/architecture (or the deterministic authored fallback). */
  primary: NormalizedDownloadOption
  /** All deduplicated options, ordered for display in the ActionMenu overlay. */
  items: NormalizedDownloadOption[]
}

function dedupeById<T extends {sys: {id: string}}>(items: readonly T[]): T[] {
  const seenIds = new Set<string>()
  const deduped: T[] = []

  for (const item of items) {
    if (seenIds.has(item.sys.id)) continue
    seenIds.add(item.sys.id)
    deduped.push(item)
  }

  return deduped
}

export function dedupeDownloadOptions(downloadOptions: DownloadOptions): [DownloadOption, ...DownloadOption[]] {
  const [first, ...rest] = dedupeById(downloadOptions.fields.options)
  if (!first) throw new Error('Download options must include at least one option')

  return [first, ...rest]
}

export function toOSAwareDownloadActions(
  downloadOptions: DownloadOptions,
): [OSAwareDownloadAction, ...OSAwareDownloadAction[]] {
  const [first, ...rest] = dedupeDownloadOptions(downloadOptions)
  const toAction = (option: DownloadOption): OSAwareDownloadAction => ({
    id: option.sys.id,
    platform: PLATFORM_TO_OS[option.fields.platform],
    architecture: option.fields.architecture,
    label: option.fields.link.fields.text,
    href: option.fields.link.fields.href,
    openInNewTab: option.fields.link.fields.openInNewTab,
  })

  return [toAction(first), ...rest.map(toAction)]
}

export function toResolvedDownloadOptions(
  downloadOptions: DownloadOptions,
  resolved: ResolvedDownloadActions,
): ResolvedDownloadOptions {
  const optionsById = new Map(dedupeDownloadOptions(downloadOptions).map(option => [option.sys.id, option]))
  const toContentfulItem = (item: ResolvedDownloadActions['items'][number]): NormalizedDownloadOption => {
    const option = optionsById.get(item.action.id)
    if (!option) throw new Error(`Missing Contentful download option for action "${item.action.id}"`)

    return {platform: item.platform, arch: item.arch, option}
  }

  return {
    primary: toContentfulItem(resolved.primary),
    items: resolved.items.map(toContentfulItem),
  }
}

/**
 * Resolves the deduplicated download options for the given detected OS and
 * (optional) architecture. Never mutates `downloadOptions`.
 *
 * - `primary` matches on `resolvedOS` and `resolvedArch` when available.
 *   Without architecture, the first authored option for the resolved OS wins.
 *   When nothing matches, the first authored option is the deterministic
 *   fallback for SSR and unknown or unsupported clients.
 * - `items` preserves authored order when `resolvedOS` is `OS.Unknown`
 *   (nothing meaningful to sort by yet), and otherwise uses `sortByOS` to
 *   return a new, OS/arch-ordered copy of the normalized list for display in
 *   the ActionMenu overlay.
 */
export function resolveDownloadOptions(
  downloadOptions: DownloadOptions,
  resolvedOS: OS,
  resolvedArch?: Architecture,
): ResolvedDownloadOptions {
  const resolved = resolveDownloadActions(toOSAwareDownloadActions(downloadOptions), resolvedOS, resolvedArch)
  return toResolvedDownloadOptions(downloadOptions, resolved)
}
